/* eslint-disable jsdoc/require-jsdoc */
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { ThemeContext } from '../../context/theme.context';
import { pinnedTooltipsPlugin } from '../../chartPlugins';
import {
  ClearPinsButton,
  usePinnedTooltips,
  withPinnedTooltips
} from '../../hooks/usePinnedTooltips';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  {
    id: 'uniqueid5',
    afterDraw(chart: any) {
      // eslint-disable-next-line no-underscore-dangle
      if (chart.tooltip._active && chart.tooltip._active.length) {
        // eslint-disable-next-line no-underscore-dangle
        const activePoint = chart.tooltip._active[0];
        const { ctx } = chart;
        const { x } = activePoint.element;
        const topY = chart.scales.y.top;
        const bottomY = chart.scales.y.bottom;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#475467';
        ctx.stroke();
        ctx.restore();
      }
    }
  }
);
type props = {
  deviceData: {
    [key: number]: { value: number[][]; bordercolor: string };
  };
  labels: string[];
  hideTicks?: boolean;
  dashed?: boolean;
  /** Force a single accent colour for every series (overrides per-series colour). */
  lineColor?: string;
  /** Fill the area under the line with a fading gradient. */
  fill?: boolean;
  /** Label shown before the value in the tooltip (e.g. "AQI"). */
  tooltipLabel?: string;
};

/**
 * Function to get step size by min and max value.
 * @param min - Minimum value.
 * @param max - Maximum value.
 * @returns - Number.
 */
function getStepSize(min: number, max: number): number {
  const difference = max - min;
  const timesToAdd = Math.ceil(difference / 6);
  const multiplier = Math.ceil(difference / timesToAdd);

  return multiplier;
}
getStepSize(4, 7);

/**
 * Graph component that renders a line chart using Chart.js and React-Chartjs-2.
 * @template T - The type of data values in the chart.
 * @param props - The component props.
 * @param  props.deviceData - An array of data values to be displayed on the chart.
 * @param props.labels - Xaxis labels.
 * @param  props.hideTicks - Show or Hide ticks.
 * @param  props.dashed - Dashed grid.
 * @param  props.lineColor - Forces a single accent colour for every series.
 * @param  props.fill - Fills the area under the line with a fading gradient.
 * @param  props.tooltipLabel - Label shown before the value in the tooltip.
 * @returns  A React element representing the Graph component.
 */
export default function Graph({
  deviceData,
  labels,
  hideTicks,
  dashed,
  lineColor,
  fill,
  tooltipLabel
}: props): ReactElement {
  // TODO:have to figure out a way to pass the tailwindcss' color classes for colors in levelcolor,gridcolor.
  const [levelcolor, setLevelColor] = useState('#DOD5DD');
  const [gridcolor, setGridColor] = useState('#F2F4F7');
  const { isDarkTheme } = useContext(ThemeContext);
  const [graphData, setGraphData] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [yaxisMinMax, setYaxisMinMax] = useState({ min: 0, max: 100 });
  const chartRef = useRef<any>(null);

  useEffect(() => {
    const datasetsArray: any[] = [];
    Object.entries(deviceData).forEach(([key, device]: any) => {
      const lc = lineColor || device.bordercolor;
      const graphDatasetValuesPerParameter: any = {
        spanGaps: true,
        label: key,
        tension: 0.35,
        data: [],
        borderColor: lc,
        borderWidth: 2,
        fill: fill ? 'origin' : false,
        backgroundColor: fill
          ? (context: any) => {
              const { chart } = context;
              const { ctx: c, chartArea } = chart;
              if (!chartArea) return 'transparent';
              const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0, `${lc}59`);
              gradient.addColorStop(1, `${lc}00`);
              return gradient;
            }
          : lc,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: '#ffffff',
        pointHoverBorderColor: lc,
        pointHoverBorderWidth: 2
      };
      const values = device.value;
      // const yAxisValue = values
      //   .filter((item:any) => item.length > 1)
      //   .map((item:any) => {
      //     return item[1];
      //   });
      graphDatasetValuesPerParameter.data = values;
      // parametersData[parameter.name].value.forEach((item: any) => {
      //   graphDatasetValuesPerParameter.data.push(item);
      // });
      datasetsArray.push(graphDatasetValuesPerParameter);
      // const maxValue = Math.max(...yAxisValue);
      // const buffer = 0.1;
      // setYaxisMinMax({
      //   min: Math.floor(Math.min(...yAxisValue)),
      //   max: maxValue * (1 + buffer)
      // });
    });
    setGraphData([...datasetsArray]);
  }, [deviceData, lineColor, fill]);

  useEffect(() => {
    if (isDarkTheme) {
      setLevelColor('#475467');
      setGridColor('#101828');
    } else {
      setLevelColor('#D0D5DD');
      setGridColor('#F2F4F7');
    }
  }, [isDarkTheme]);

  const data = {
    labels,
    datasets: graphData
  };

  const options: any = {
    responsive: true,
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    hover: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: false,
        position: 'top' as const
      },
      title: {
        display: false
      },
      tooltip: {
        usePointStyle: true,
        backgroundColor: '#1b1e2b',
        borderColor: '#2a2e3f',
        borderWidth: 1,
        titleColor: '#98A2B3',
        bodyColor: '#ffffff',
        titleFont: { size: 12, weight: '400' },
        bodyFont: { size: 14, weight: '600' },
        padding: 12,
        cornerRadius: 10,
        displayColors: false,
        callbacks: {
          label: (ctx: any) =>
            `${tooltipLabel ? `${tooltipLabel}: ` : ''}${ctx.parsed.y}`
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
          // Change the color of the level labels
        },
        ticks: {
          color: levelcolor,
          maxTicksLimit: 5,
          display: !hideTicks
        }
      },
      y: {
        min: 0,
        grid: {
          display: true,
          color: gridcolor // Change the color of the x-axis lines
        },
        border: {
          dash: !dashed ? [0, 0] : [4, 2]
        },
        beginAtZero: true,
        ticks: {
          color: levelcolor, // Change the color of the level labels,
          display: !hideTicks
        }
      }
    },
    maintainAspectRatio: false
  };

  // Clicking a point pins its tooltip; click it again (or "Clear pins") to
  // drop it. Options are only rebuilt when the chart's own options change.
  const pinned = usePinnedTooltips({ chartRef, resetKey: graphData });
  const chartOptions = useMemo(
    () => withPinnedTooltips(options, pinned),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, pinned.onChartClick, pinned.pluginOptions]
  );

  return (
    <div className="relative text-gray-500 w-full h-full">
      <Line
        ref={chartRef}
        options={chartOptions}
        data={data}
        plugins={[pinnedTooltipsPlugin]}
      />
      {pinned.pins.length > 0 && (
        <div className="absolute right-2 top-2 z-10">
          <ClearPinsButton count={pinned.pins.length} onClear={pinned.clear} />
        </div>
      )}
    </div>
  );
}
